import React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  ActionGroup,
  ActionGroupItem,
} from '@app/common/components/ActionGroup';
import { OverlaySpinner } from '@app/common/components/overlayspinner';
import { FieldComponent, FieldInfoGroupField } from '@app/common/configurable-wizards';
import { combineNames } from '@app/common/configurable-wizards/utils';

import { isEmbeddedMode } from '@app/core/authentication/embeddedMode/embeddedMode';

import { useUserSettingsLocator } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsLocator';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';
import { UrlParams } from '@app/widgets/contacts-and-settings/types';
import { getUrlToField } from '@app/widgets/contacts-and-settings/utils/paths';
import { useGetLoginSettingsQuery } from '@app/widgets/user-login/LoginWizard/steps/UsernamePassword/queries/queryTyping/get-login-settings';
import { checkPasskeyAvaialble } from '@app/widgets/user-login/LoginWizard/steps/UsernamePassword/utils';

import styles from '../ProfileAndSecurityFieldsList.pcss';

import { DisplayPasskeyFieldLocator, passkeyField } from '../Passkeys/DisplayPasskeyField';
import { DisplayPasswordFieldLocator } from './Password/DisplayPasswordField';
import { DisplayUserEventLogsFieldLocator, userEventsLogsField } from './UserEventLogs/DisplayUserEventLogField';

/**
 * Return true if passkey is enabled and available and not in embedded mode, as
 * passkey settings are temporary exluded for native apps.
 */
export const isShowPasskeySection = (isPasskeyEnabled: boolean, isPasskeyAvailable: boolean, isEmbedded: boolean) => (
  isPasskeyEnabled && isPasskeyAvailable && !isEmbedded
);

export const SecurityList: FieldComponent<FieldInfoGroupField> = ({ parent, field }) => {
  const { t } = useUserSettingsTranslation();
  const { sectionId } = useParams<UrlParams>();
  const history = useHistory();

  // get current login settings
  const { loading, data: loginSettingsData } = useGetLoginSettingsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const locator = useUserSettingsLocator([
    DisplayPasswordFieldLocator,
    DisplayPasskeyFieldLocator,
    DisplayUserEventLogsFieldLocator,
  ]);

  if (loading) {
    return (
      <OverlaySpinner
        visible
      />
    );
  }

  const isPasskeyEnabled = loginSettingsData?.loginSettings.passkeyLoginSettings.isEnabled;
  const fields = [
    ...field.subFields,
    isShowPasskeySection(!!isPasskeyEnabled, checkPasskeyAvaialble(), isEmbeddedMode()) ? passkeyField : null,
    userEventsLogsField,
  ].filter(Boolean);

  return (
    <div className={styles.group}>
      <h3 className={styles.title}>
        {t(
          'profile-and-security.security-group.title|Security group title text',
          'Security',
        )}
      </h3>
      <ActionGroup>
        {fields.map((item, index) => {
          const subField = item as FieldInfoGroupField;
          const Component = locator(subField);

          return (
            <ActionGroupItem
              data-stable-name={`${subField.id}Item`}
              className={styles.groupItem}
              iconClassName={styles.groupItemIcon}
              key={subField.id || index}
              onSelect={() => {
                history.push(
                  getUrlToField(
                    sectionId,
                    subField.id,
                  ),
                );
              }}
            >
              <Component
                parent={combineNames(parent, field.id)}
                field={subField}
                locator={locator}
              />
            </ActionGroupItem>
          );
        })}
      </ActionGroup>
    </div>
  );
};
